/**
 * Sticky header tools.
 */
H_Tools.stickyheader = (() => {
    /**
     * Margin between header and element when scrolling backwards to an element.
     */
    const scrollMargin = 24;

    /**
     * WordPress admin bar height in desktop.
     */
    const wordPressAdminBarHeightDesktop = 32;

    /**
     * How much an element can be outside the visible viewport before it is considered to be outside of it.
     */
    const errorMargin = 16;

    /**
     * Checks if the supplied element is visible. Returns a representative boolean value.
     *
     * The element is considered visible if:
     * 1. It is inside the viewport (barring an error margin).
     * 2. It is not under the sticky header.
     *
     * @param {*} el The element to examine
     * @returns      `true` if the element is visible, `false` otherwise
     */
    function isElementVisible(el) {
        let rect = el.getBoundingClientRect();
        const isVisible =
            rect.top >= H_Tools?.header?.height &&
            rect.bottom <=
                (window.innerHeight || document.documentElement.clientHeight) +
                    errorMargin;
        return isVisible;
    }

    /**
     * Scrolls the window so that the supplied element:
     *
     * 1. Is inside the viewport.
     * 2. Is not under the sticky header.
     *
     * Note that this function uses a scroll margin, to make some room between the sticky header and
     * the element.
     *
     * @param {*} el              The element to scroll to
     * @param     useScrollMargin Whether or not to use scroll margin, that is; a margin between
     *                            header and element when scrolling backwards to an element
     */
    function scrollTo(el, useScrollMargin = true) {
        if (!el) {
            return;
        }

        const elementPosition = el.getBoundingClientRect().top;
        const scrollMarginActual = useScrollMargin ? scrollMargin : 0;

        const adminBarHeight = document.body.classList.contains('admin-bar')
            ? wordPressAdminBarHeightDesktop
            : 0;

        const offsetPosition =
            elementPosition +
            window.scrollY -
            H_Tools?.header?.height -
            scrollMarginActual -
            adminBarHeight;

        window.scrollTo({
            top: offsetPosition,
        });
    }

    /**
     * Makes all focusable children of the supplied elements backwards focusable when the sticky
     * header is enabled.
     *
     * This function may need to be reaplied if the DOM is manipulated using JavaScript.
     *
     * @param {*} elements One or more elements to process
     */
    function makeBackwardsFocusable(...elements) {
        for (let element of elements) {
            H_Tools.focus.getFocusableChildren(element).forEach((child) => {
                /**
                 * Prevent adding the listener more than once by checking and setting an expando
                 * property on the element.
                 *
                 * @see https://developer.mozilla.org/en-US/docs/Glossary/Expando
                 */
                if (child.hasBackwardsFocusListener) {
                    return;
                }
                child.hasBackwardsFocusListener = true;

                child.addEventListener('focus', (e) => {
                    // console.log('Backwards focus listener triggered');

                    // Allow disabling the listener for certain elements
                    if (
                        e.target.classList.contains('js-backwards-focus-ignore')
                    ) {
                        return;
                    }

                    if (!isElementVisible(e.target)) {
                        scrollTo(e.target);
                    }
                });
            });
        }
    }

    // return an object to expose inner functions
    return {
        isElementVisible: isElementVisible,
        makeBackwardsFocusable: makeBackwardsFocusable,
        scrollTo: scrollTo,
    };
})();
